import React from 'react';
import { Skeleton, Stack, Box } from '@mui/material';

export interface ToolCardSkeletonProps {
  /**
   * Whether to show the checkbox skeleton
   */
  showCheckbox?: boolean;
  /**
   * Animation type for the skeleton
   */
  animation?: 'pulse' | 'wave' | false;
}

/**
 * Skeleton placeholder for ToolCard while loading
 * Matches the visual structure of the actual ToolCard
 */
export const ToolCardSkeleton: React.FC<ToolCardSkeletonProps> = ({
  showCheckbox = false,
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 72,
      }}
    >
      {/* Checkbox placeholder */}
      {showCheckbox && (
        <Skeleton
          variant="rectangular"
          width={20}
          height={20}
          animation={animation}
          sx={{ borderRadius: 0.5, flexShrink: 0 }}
        />
      )}

      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={40}
        height={40}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Skeleton variant="text" width="60%" height={24} animation={animation} />
        {/* Description */}
        <Skeleton variant="text" width="80%" height={18} animation={animation} />
      </Box>

      {/* Status chip placeholder */}
      <Skeleton
        variant="rectangular"
        width={60}
        height={24}
        animation={animation}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />

      {/* Action buttons placeholder */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
      </Stack>
    </Stack>
  );
};

/**
 * Skeleton placeholder for toolset cards while loading
 */
export const ToolsetCardSkeleton: React.FC<{ animation?: 'pulse' | 'wave' | false }> = ({
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 80,
      }}
    >
      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={44}
        height={44}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Skeleton variant="text" width="50%" height={24} animation={animation} />
        {/* Description */}
        <Skeleton variant="text" width="70%" height={18} animation={animation} />
        {/* Tools count / badges */}
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={20}
            animation={animation}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={60}
            height={20}
            animation={animation}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
      </Box>

      {/* Status chip placeholder */}
      <Skeleton
        variant="rectangular"
        width={70}
        height={24}
        animation={animation}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />

      {/* Action buttons placeholder */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
      </Stack>
    </Stack>
  );
};

/**
 * Skeleton placeholder for MCP server cards while loading
 */
export const MCPServerCardSkeleton: React.FC<{ animation?: 'pulse' | 'wave' | false }> = ({
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 88,
      }}
    >
      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={48}
        height={48}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Skeleton variant="text" width="45%" height={24} animation={animation} />
        {/* Endpoint URL */}
        <Skeleton variant="text" width="60%" height={18} animation={animation} />
        {/* Info badges */}
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Skeleton
            variant="rectangular"
            width={90}
            height={20}
            animation={animation}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={70}
            height={20}
            animation={animation}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
      </Box>

      {/* Status chip placeholder */}
      <Skeleton
        variant="rectangular"
        width={65}
        height={24}
        animation={animation}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />

      {/* Action buttons placeholder */}
      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
        <Skeleton variant="circular" width={32} height={32} animation={animation} />
      </Stack>
    </Stack>
  );
};

/**
 * Skeleton placeholder for nexset list items in selectors
 * Compact design for list-style items with checkbox
 */
export const NexsetItemSkeleton: React.FC<{ animation?: 'pulse' | 'wave' | false }> = ({
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 56,
      }}
    >
      {/* Checkbox placeholder */}
      <Skeleton
        variant="rectangular"
        width={18}
        height={18}
        animation={animation}
        sx={{ borderRadius: 0.5, flexShrink: 0 }}
      />

      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={32}
        height={32}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name */}
        <Skeleton variant="text" width="55%" height={20} animation={animation} />
        {/* Description */}
        <Skeleton variant="text" width="75%" height={16} animation={animation} />
      </Box>

      {/* Status indicator */}
      <Skeleton
        variant="circular"
        width={8}
        height={8}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  );
};

/**
 * Skeleton placeholder for tool list items in selectors
 * Compact design for list-style items with checkbox
 */
export const ToolItemSkeleton: React.FC<{ animation?: 'pulse' | 'wave' | false }> = ({
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 60,
      }}
    >
      {/* Checkbox placeholder */}
      <Skeleton
        variant="rectangular"
        width={18}
        height={18}
        animation={animation}
        sx={{ borderRadius: 0.5, flexShrink: 0 }}
      />

      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={36}
        height={36}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name */}
        <Skeleton variant="text" width="50%" height={20} animation={animation} />
        {/* Description */}
        <Skeleton variant="text" width="70%" height={16} animation={animation} />
      </Box>

      {/* Type badge */}
      <Skeleton
        variant="rectangular"
        width={50}
        height={22}
        animation={animation}
        sx={{ borderRadius: 1, flexShrink: 0 }}
      />
    </Stack>
  );
};

/**
 * Skeleton placeholder for toolset list items in selectors
 * Slightly larger than tool items to show tools count
 */
export const ToolsetItemSkeleton: React.FC<{ animation?: 'pulse' | 'wave' | false }> = ({
  animation = 'wave',
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.5,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: 64,
      }}
    >
      {/* Checkbox placeholder */}
      <Skeleton
        variant="rectangular"
        width={18}
        height={18}
        animation={animation}
        sx={{ borderRadius: 0.5, flexShrink: 0 }}
      />

      {/* Icon placeholder */}
      <Skeleton
        variant="circular"
        width={36}
        height={36}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name */}
        <Skeleton variant="text" width="45%" height={20} animation={animation} />
        {/* Description */}
        <Skeleton variant="text" width="65%" height={16} animation={animation} />
        {/* Tools count */}
        <Skeleton variant="text" width="30%" height={14} animation={animation} />
      </Box>

      {/* View tools button */}
      <Skeleton
        variant="circular"
        width={28}
        height={28}
        animation={animation}
        sx={{ flexShrink: 0 }}
      />
    </Stack>
  );
};

/**
 * Skeleton placeholder for code/config blocks
 * Used for MCP config and JSON displays
 */
export const ConfigBlockSkeleton: React.FC<{
  animation?: 'pulse' | 'wave' | false;
  lines?: number;
}> = ({ animation = 'wave', lines = 8 }) => {
  // Generate varying line widths for realistic code appearance
  const lineWidths = [85, 60, 70, 95, 50, 80, 65, 90, 55, 75];

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header with copy button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Skeleton variant="text" width={120} height={20} animation={animation} />
        <Skeleton
          variant="rectangular"
          width={28}
          height={28}
          animation={animation}
          sx={{ borderRadius: 0.5 }}
        />
      </Stack>

      {/* Code lines */}
      <Stack spacing={0.75}>
        {Array.from({ length: lines }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={`${lineWidths[index % lineWidths.length]}%`}
            height={16}
            animation={animation}
            sx={{
              bgcolor: 'grey.200',
              ml: index % 3 === 0 ? 0 : index % 3 === 1 ? 2 : 4, // Simulate indentation
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Helper to render multiple skeleton items
 */
export const SkeletonList: React.FC<{
  count: number;
  component: React.FC<any>;
  spacing?: number;
  componentProps?: any;
}> = ({ count, component: Component, spacing = 1.5, componentProps = {} }) => {
  return (
    <Stack spacing={spacing}>
      {Array.from({ length: count }).map((_, index) => (
        <Component key={index} {...componentProps} />
      ))}
    </Stack>
  );
};

export default ToolCardSkeleton;
